/**
 * The forwarding-address card (§ B8) — "email your itinerary to TripMuscle".
 *
 * Shows the traveller their unique address, lets them copy it, rotate it, and
 * see what has arrived. The address is the whole feature's surface area: if a
 * traveller cannot find it, the ingestion pipeline behind it is unreachable.
 *
 * ⚠️ The address is a BEARER SECRET — anything delivered to it is attributed to
 * this account. That is why "Rotate" exists and why the copy says what it says:
 * a traveller who pastes it into a public forum needs to understand that is a
 * problem, and needs the one-click fix in the same place.
 */

import { useState, useEffect, useCallback } from 'react';
import { Mail, Copy, Check, RefreshCw, Loader2, AlertTriangle, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuthStore } from '@/lib/auth';
import {
  presentStatus,
  summarise,
  describeLastRun,
  type ForwardedMessage,
} from './forwardingView';

interface ForwardingSettings {
  forwardingAddress: string | null;
  tokenIssuedAt: string | null;
  configured: boolean;
  lastRun: string | null;
  pendingCount: number;
  recordedCount: number;
}

const TONE_CLASS: Record<string, string> = {
  good: 'text-emerald-600',
  attention: 'text-amber-600',
  inert: 'text-muted-foreground',
  bad: 'text-red-600',
};

export default function ForwardingAddressCard() {
  const token = useAuthStore((s) => s.token);
  const [settings, setSettings] = useState<ForwardingSettings | null>(null);
  const [messages, setMessages] = useState<ForwardedMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const load = useCallback(async () => {
    try {
      const [sRes, mRes] = await Promise.all([
        fetch('/api/booking-email/settings', { headers: authHeaders() }),
        fetch('/api/booking-email/messages?limit=10', { headers: authHeaders() }),
      ]);
      if (sRes.ok) setSettings(await sRes.json());
      if (mRes.ok) setMessages((await mRes.json()).messages || []);
    } catch {
      setError('Could not load your forwarding address.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  async function copyAddress() {
    if (!settings?.forwardingAddress) return;
    try {
      await navigator.clipboard.writeText(settings.forwardingAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be denied; the address is visible and selectable anyway,
      // so this is not worth an error banner.
    }
  }

  async function rotate() {
    // Rotation invalidates the old address immediately. Confirm, because a
    // traveller with a mail rule pointing at the old one will silently stop
    // being ingested until they update it.
    if (
      !window.confirm(
        'Issue a new forwarding address? The current one stops working immediately, ' +
          'so any auto-forward rule using it will need updating.'
      )
    ) {
      return;
    }
    setRotating(true);
    setError(null);
    try {
      const res = await fetch('/api/booking-email/settings/rotate', {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('rotate failed');
      await load();
    } catch {
      setError('Could not issue a new address. Nothing has changed.');
    } finally {
      setRotating(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your forwarding address…
        </CardContent>
      </Card>
    );
  }

  const lastRun = describeLastRun(settings?.lastRun);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" /> Email your bookings in
        </CardTitle>
        <CardDescription>
          Forward an airline confirmation — or a hotel or activity booking — to your own
          TripMuscle address and we will work out which trip and which leg it belongs to.
          We only ever see what you forward.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {!settings?.configured && (
          <div className="p-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Email forwarding is not configured on this server, so nothing sent to the
              address below would be read. Your address is reserved and will start working
              as soon as it is.
            </span>
          </div>
        )}

        {settings?.forwardingAddress && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded-lg bg-muted font-mono text-sm break-all">
                {settings.forwardingAddress}
              </code>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyAddress}>
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </>
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={rotate} disabled={rotating}>
                  {rotating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span className="ml-1 hidden sm:inline">Rotate</span>
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Treat this like a password — anything sent to it is added to your account.
              Rotate it if it ever gets out.
            </p>
          </div>
        )}

        <div className="text-sm text-muted-foreground">
          {summarise({
            configured: !!settings?.configured,
            pendingCount: settings?.pendingCount ?? 0,
            recordedCount: settings?.recordedCount ?? 0,
          })}
          {lastRun && <span className="ml-1">Last checked {lastRun}.</span>}
        </div>

        {messages.length > 0 && (
          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Inbox className="h-3 w-3" /> Recently forwarded
            </div>
            {messages.map((m) => {
              const p = presentStatus(m.status);
              return (
                <div key={m.id} className="text-sm flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate">{m.subject || '(no subject)'}</div>
                    {m.status_detail && (
                      <div className="text-xs text-muted-foreground truncate">
                        {m.status_detail}
                      </div>
                    )}
                  </div>
                  <div className={`text-xs shrink-0 ${TONE_CLASS[p.tone]}`}>{p.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
